/**
 * Cookie Consent Management Utility
 * Handles storage and retrieval of user cookie consent preferences
 * Compliant with GDPR, CCPA, and other privacy regulations
 */

const CONSENT_COOKIE_NAME = 'ba_cookie_consent';
const CONSENT_VERSION = '1.0';
const CONSENT_TTL_DAYS = 180;

/**
 * Default consent structure
 */
export const CONSENT_CATEGORIES = {
  necessary: true, // Always true, cannot be toggled
  preferences: false,
  analytics: false,
  marketing: false,
};

/**
 * Category descriptions for the UI
 */
export const CONSENT_DESCRIPTIONS = {
  necessary: {
    title: 'Necessary Cookies',
    description: 'These cookies are essential for the website to function properly. They enable basic features like page navigation, secure areas access, and shopping cart functionality. The website cannot function properly without these cookies.',
  },
  preferences: {
    title: 'Preference Cookies',
    description: 'Preference cookies enable the website to remember information that changes the way the website behaves or looks, like your preferred language, region, or UI customizations.',
  },
  analytics: {
    title: 'Analytics Cookies',
    description: 'Analytics cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve our services and user experience.',
  },
  marketing: {
    title: 'Marketing Cookies',
    description: 'Marketing cookies are used to track visitors across websites. The intention is to display ads that are relevant and engaging for the individual user and thereby more valuable for publishers and third-party advertisers.',
  },
};

/**
 * Set a cookie with proper encoding and expiration
 */
function setCookie(name, value, days) {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = `expires=${date.toUTCString()}`;

  // Set cookie with proper attributes for security and cross-subdomain access
  // Domain is set to .bookatherapy.com to work across all subdomains
  const domain = window.location.hostname.includes('bookatherapy.com')
    ? 'domain=.bookatherapy.com'
    : '';

  document.cookie = `${name}=${encodeURIComponent(value)};${expires};path=/;${domain};SameSite=Lax;Secure`;
}

/**
 * Get a cookie value by name
 */
function getCookie(name) {
  const nameEQ = `${name}=`;
  const cookies = document.cookie.split(';');

  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i];
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1, cookie.length);
    }
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length, cookie.length));
    }
  }
  return null;
}

/**
 * Delete a cookie
 */
function deleteCookie(name) {
  const domain = window.location.hostname.includes('bookatherapy.com')
    ? 'domain=.bookatherapy.com'
    : '';

  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;${domain}`;
}

/**
 * Save consent preferences
 */
export function saveConsent(preferences) {
  const consentData = {
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
    preferences: {
      ...CONSENT_CATEGORIES,
      ...preferences,
      necessary: true, // Always enforce necessary cookies
    },
  };

  try {
    setCookie(CONSENT_COOKIE_NAME, JSON.stringify(consentData), CONSENT_TTL_DAYS);

    // Also save to localStorage as backup
    localStorage.setItem(CONSENT_COOKIE_NAME, JSON.stringify(consentData));

    // Trigger custom event for other parts of the app to listen to
    window.dispatchEvent(new CustomEvent('consentUpdated', {
      detail: consentData.preferences
    }));

    return true;
  } catch (error) {
    console.error('Error saving consent preferences:', error);
    return false;
  }
}

/**
 * Get current consent preferences
 */
export function getConsent() {
  try {
    // Try to get from cookie first
    const cookieData = getCookie(CONSENT_COOKIE_NAME);
    if (cookieData) {
      const parsed = JSON.parse(cookieData);

      // Check if consent has expired (180 days)
      const consentDate = new Date(parsed.timestamp);
      const now = new Date();
      const daysSinceConsent = (now - consentDate) / (1000 * 60 * 60 * 24);

      if (daysSinceConsent > CONSENT_TTL_DAYS) {
        // Consent expired, clear it
        clearConsent();
        return null;
      }

      // Check version compatibility
      if (parsed.version !== CONSENT_VERSION) {
        // Version mismatch, clear and re-prompt
        clearConsent();
        return null;
      }

      return parsed.preferences;
    }

    // Fallback to localStorage
    const localData = localStorage.getItem(CONSENT_COOKIE_NAME);
    if (localData) {
      const parsed = JSON.parse(localData);

      // Check expiration
      const consentDate = new Date(parsed.timestamp);
      const now = new Date();
      const daysSinceConsent = (now - consentDate) / (1000 * 60 * 60 * 24);

      if (daysSinceConsent > CONSENT_TTL_DAYS || parsed.version !== CONSENT_VERSION) {
        clearConsent();
        return null;
      }

      return parsed.preferences;
    }

    return null;
  } catch (error) {
    console.error('Error retrieving consent preferences:', error);
    return null;
  }
}

/**
 * Check if user has consented to a specific category
 */
export function hasConsent(category) {
  const consent = getConsent();
  if (!consent) return false;

  // Necessary cookies are always allowed
  if (category === 'necessary') return true;

  return consent[category] === true;
}

/**
 * Clear all consent data
 */
export function clearConsent() {
  try {
    deleteCookie(CONSENT_COOKIE_NAME);
    localStorage.removeItem(CONSENT_COOKIE_NAME);

    window.dispatchEvent(new CustomEvent('consentCleared'));

    return true;
  } catch (error) {
    console.error('Error clearing consent:', error);
    return false;
  }
}

/**
 * Accept all consent categories
 */
export function acceptAllConsent() {
  return saveConsent({
    necessary: true,
    preferences: true,
    analytics: true,
    marketing: true,
  });
}

/**
 * Reject all optional consent categories (only necessary cookies)
 */
export function rejectAllConsent() {
  return saveConsent({
    necessary: true,
    preferences: false,
    analytics: false,
    marketing: false,
  });
}

/**
 * Check if consent banner should be shown
 */
export function shouldShowConsentBanner() {
  return getConsent() === null;
}

/**
 * Listen to consent changes
 */
export function onConsentChange(callback) {
  const handler = (event) => {
    callback(event.detail);
  };

  window.addEventListener('consentUpdated', handler);

  // Return cleanup function
  return () => window.removeEventListener('consentUpdated', handler);
}
